import { useState } from 'react';
import {
	Box,
	BoxProps,
	FormControl,
	MenuItem,
	Select,
	selectClasses,
	SvgIconProps
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useTheme } from "@mui/material/styles";

type SimpleDateFilterOption = { value: string; label: string };

type SimpleDateFilterProps = {
	filterMonth: string;
	setFilterMonth: React.Dispatch<React.SetStateAction<string>>;
	handleChange: (selectedDate: string) => void;
	boxProps?: BoxProps;
	disableFilter?: boolean;
	renderValue?: (value: string) => string;
	isPowerBIDashboard?: boolean;
	customOptions?: SimpleDateFilterOption[];
};

const iconProps: SvgIconProps = {
	sx: {
		cursor: 'pointer',
	}
};

const NoIcon = () => null;

const SimpleDateFilter = ({
	filterMonth,
	setFilterMonth,
	handleChange,
	boxProps,
	disableFilter,
	renderValue,
	isPowerBIDashboard = false,
	customOptions
}: SimpleDateFilterProps) => {
	const theme = useTheme();
	const colors = theme.palette;
	const { sx, ...props } = boxProps || {};
	const [isDateOpen, setIsDateOpen] = useState(false);

	const handleDateFilter = (selectedDate: string) => {
		setIsDateOpen(false);
		setFilterMonth(selectedDate);
		handleChange(selectedDate);
	};

	// PowerBI specific date options
	const powerBIOptions = [
		{ value: 'TODAY', label: 'Today' },
		{ value: 'YESTERDAY', label: 'Yesterday' },
		{ value: 'LAST_7_DAYS', label: 'Last 7 Days' },
		{ value: 'LAST_30_DAYS', label: 'Last 30 Days' },
		{ value: 'LAST_60_DAYS', label: 'Last 60 Days' },
		{ value: 'LAST_90_DAYS', label: 'Last 90 Days' }
	];

	// Standard date options (fallback)
	const standardOptions = [
		{ value: '0T', label: 'Today' },
		{ value: '1T', label: 'Yesterday' },
		{ value: '7D', label: 'Last 7 Days' },
		{ value: '30D', label: 'Last 30 Days' },
		{ value: '60D', label: 'Last 60 Days' },
		{ value: '90D', label: 'Last 90 Days' }
	];

	let options:any;

	if (customOptions && customOptions.length > 0) {
		options = customOptions;
	} else if (isPowerBIDashboard) {
		options = powerBIOptions;
	} else {
		options = standardOptions;
	}


	// Helper function to get display value
	const getDisplayValue = (value: string) => {
		if (renderValue) {
			return renderValue(value);
		}

		const option = options.find((opt:any) => opt.value === value);
		return option ? option.label : value;
	};

	return (
		<Box
			sx={{
				backgroundColor: 'blue.dark',
				borderRadius: '4px',
				fontSize: '15px',
				zIndex: 999,
				minWidth: '200px',
				textAlign: 'left',
				height: 'fit-content',
				...sx
			}}
			{...props}
		>
			<FormControl fullWidth>
				<Select
					value={filterMonth}
					label="Date"
					onChange={(e) => {
						handleDateFilter(e.target.value);
					}}
					IconComponent={CalendarMonthIcon}
					open={isDateOpen}
					onOpen={() => setIsDateOpen(true)}
					onClose={() => setIsDateOpen(false)}
					renderValue={renderValue ? (value) => renderValue(value) : undefined}
					sx={{
						width: '195px',
						paddingTop: '4px',
						"& .MuiSelect-select": {
							...theme.typography.filterText,
						},
						[`& .${selectClasses.icon}`]: {
							transform: 'none',
							paddingTop: '2px',
						}
					}}
					MenuProps={{
						PaperProps: {
						sx: {
							backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
							color: `${colors.dropdown.menuItemText} !important`,
						},
						},
						MenuListProps: { sx: { p: 0 } },
					}}
					disabled={disableFilter}
				>
					{options.map((option:any) => (
						<MenuItem 
							key={option.value} 
							value={option.value}
							selected={filterMonth === option.value}
							sx={{
								...theme.typography.filterText,
								backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
								color: `${colors.dropdown.menuItemText} !important`,
								'&.Mui-selected': {
									backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
								'&.Mui-selected:hover': {
									backgroundColor: `${colors.dropdown.menuItemHover} !important`,
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
								'&:hover': {
									backgroundColor: `${colors.dropdown.menuItemHover} !important`,
									color: `${colors.dropdown.menuItemHoverText} !important`,
								},
							}}
						>
							{option.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	);
};

export default SimpleDateFilter;
